import { useMemo } from 'react';
import { connect } from 'redux-bundler-react';
import { useFormContext } from 'react-hook-form';
import classnames from 'classnames';

import { Textarea as UswdsTextArea, Label } from '@trussworks/react-uswds';

import { isValidText, sanitizeText } from '@src/utils/helpers';
import { ErrorMessages } from '@src/utils/enums';
import Tooltip from '@components/tooltip/tooltip';
import genericSecondaryModal from '@forms/components/modals/genericSecondaryModal';
import { useModal } from '@stores';
import FileDownloadList from '../file-download-list/FileDownloadList';
import { getWeightedLength, truncateToWeightedMax } from './TextArea.helper';

import './textarea.scss';

const TextArea = connect(
  ({
    className,
    defaultValue,
    fileDownload,
    hint,
    label,
    maxLength = 4000,
    name,
    onBlur,
    onChange,
    pattern,
    readOnly,
    required,
    rowCount,
    showOptionalText = true,
    tooltip,
    tooltipClickable,
    validations,
  }) => {
    const {
      formState: { errors },
      register,
      setValue,
      watch,
    } = useFormContext();
    const { openSecondaryModal } = useModal();
    const inputValue = defaultValue || watch(name);
    const hasError = !!errors[name];
    const inputStyles = classnames('form-control', className, {
      'flex-height-textarea': !rowCount,
    });
    const labelStyles = classnames({
      required: required,
      optional: !required && showOptionalText,
    });

    const {
      ref: textareaRef,
      onChange: onInputChange,
      ...rest
    } = register(name, { onChange, onBlur, pattern, ...validations });

    const countText = useMemo(() => {
      const weightedLength = getWeightedLength(inputValue || '');
      const remaining = maxLength - weightedLength;
      const endWord = (inputValue || '').length === 0 ? 'allowed' : 'left';

      return `${remaining} characters ${endWord}`;
    }, [inputValue, maxLength]);

    const handleOnChange = (e) => {
      if (typeof e.target?.value !== 'string') return;

      const weightedTrimmedValue = truncateToWeightedMax(e.target.value, maxLength);
      const isValid = isValidText(weightedTrimmedValue);
      const sanitizedValue = isValid ? weightedTrimmedValue : sanitizeText(weightedTrimmedValue);

      if (!isValid) {
        openSecondaryModal(genericSecondaryModal, {
          title: 'Invalid Characters',
          msg: ErrorMessages.InvalidCharacters,
        });
      }

      e.target.value = sanitizedValue;
      setValue(name, sanitizedValue);
      onInputChange(e);
    };

    return (
      <div className='textarea-container'>
        {label && (
          <Label className={labelStyles} htmlFor={name}>
            <span id={`${name}_label`}>{label}</span>
            {tooltip && <Tooltip clickable={tooltipClickable} content={tooltip} header={label} name={name} />}
          </Label>
        )}
        {hint && (
          <div className='usa-hint' id={`${name}_hint`}>
            {hint}
          </div>
        )}
        {fileDownload?.length > 0 && <FileDownloadList files={fileDownload} />}
        <UswdsTextArea
          aria-describedby={name}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-label={label || 'Text Area'}
          aria-required={required}
          className={inputStyles}
          defaultValue={inputValue}
          error={hasError}
          id={name}
          inputRef={textareaRef}
          maxLength={maxLength}
          pattern={pattern}
          readOnly={readOnly}
          required={required}
          rows={rowCount ? rowCount : undefined}
          onChange={handleOnChange}
          {...rest}
        />
        <div className='usa-hint'>{countText}</div>
      </div>
    );
  }
);

export default TextArea;
